import type { ChangeEvent, FormEvent } from "react";
import React from "react";
import type { TFunction } from "i18next";
import type { ParentFormErrors, ParentFormField, ParentFormValues } from "../types";
import { FaEnvelope, FaHome, FaSave, FaTimes, FaUser } from "react-icons/fa";
import DutchAddressFields from "../../../components/DutchAddressFields";

interface ParentFormProps {
  formId?: string;
  hideActions?: boolean;
  values: ParentFormValues;
  errors: ParentFormErrors;
  saving?: boolean;
  mode: "create" | "edit";
  onChange: (field: ParentFormField, value: string | boolean) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onCancel: () => void;
  t: TFunction;
}

interface TextFieldConfig {
  name: Exclude<ParentFormField, "newsletterOptOut">;
  label: string;
  type: "text" | "email" | "tel";
  required?: boolean;
  wide?: boolean;
}

const inputClass =
  "mt-1 h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-950 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-200 dark:border-white/10 dark:bg-slate-950/60 dark:text-white dark:focus:border-indigo-300";
const labelClass = "text-xs font-semibold text-slate-500 dark:text-slate-300";
const errorClass = "mt-1 text-xs font-semibold text-red-600 dark:text-red-300";

const errorMessage = (field: ParentFormField, code: string | undefined, t: TFunction): string | null => {
  if (!code) return null;
  if (code === "invalid" && field === "email") {
    return t("signup.email_invalid", { defaultValue: "Enter a valid email address." });
  }
  if (code === "required") {
    return t("family.parents.common.field_required", { defaultValue: "This field is required." });
  }
  return code;
};

export default function ParentForm({
  formId,
  hideActions = false,
  values,
  errors,
  saving = false,
  mode,
  onChange,
  onSubmit,
  onCancel,
  t,
}: ParentFormProps) {
  const fields: TextFieldConfig[] = [
    { name: "firstName", label: t("common.given_name"), type: "text", required: true },
    { name: "lastName", label: t("common.family_name"), type: "text", required: true },
    { name: "email", label: t("profile.email"), type: "email", required: true },
    {
      name: "phone",
      label: mode === "create" ? t("family.common.phone") : t("family.parents.common.phone"),
      type: "tel",
    },
    {
      name: "address",
      label: mode === "create" ? t("family.common.address") : t("family.parents.common.address"),
      type: "text",
      wide: mode === "edit",
    },
    {
      name: "zipCode",
      label: mode === "create" ? t("family.common.zip_code") : t("family.parents.common.zip_code"),
      type: "text",
      wide: mode === "edit",
    },
    {
      name: "city",
      label: mode === "create" ? t("family.common.city") : t("family.parents.common.city"),
      type: "text",
      wide: mode === "edit",
    },
  ];

  const handleTextChange = (field: ParentFormField) => (event: ChangeEvent<HTMLInputElement>) => {
    onChange(field, event.target.value);
  };

  const renderField = (field: TextFieldConfig, className = "") => {
    const message = errorMessage(field.name, errors[field.name], t);
    return (
      <div key={field.name} className={className}>
        <label className={labelClass}>
          {field.label}
          {field.required ? " *" : ""}
        </label>
        <input
          type={field.type}
          name={field.name}
          value={values[field.name]}
          onChange={handleTextChange(field.name)}
          className={`${inputClass} ${message ? "border-red-400 focus:border-red-400 focus:ring-red-200" : ""}`}
          required={field.required}
          aria-invalid={Boolean(message)}
        />
        {message && <p className={errorClass}>{message}</p>}
      </div>
    );
  };

  const personalFields = fields.filter((field) => ["firstName", "lastName"].includes(field.name));
  const contactFields = fields.filter((field) => ["email", "phone"].includes(field.name));
  return (
    <form
      id={formId}
      onSubmit={onSubmit}
      className="rounded-[24px] border border-slate-200/80 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/5 sm:p-5"
    >
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1.3fr)_minmax(320px,0.8fr)]">
        <div className="space-y-5">
          <section className="rounded-[18px] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-white/10">
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-200">
                <FaUser aria-hidden="true" />
              </span>
              <h2 className="text-lg font-bold text-slate-950 dark:text-white">
                {t("family.parents.edit.tabs.personal", { defaultValue: "Personal details" })}
              </h2>
            </div>
            <div className="grid gap-5 md:grid-cols-2">
              {personalFields.map((field) => renderField(field))}
            </div>
          </section>

          <section className="rounded-[18px] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-white/10">
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-sky-50 text-sky-600 dark:bg-sky-400/15 dark:text-sky-200">
                <FaEnvelope aria-hidden="true" />
              </span>
              <h2 className="text-lg font-bold text-slate-950 dark:text-white">
                {t("family.parents.edit.tabs.contact", { defaultValue: "Contact details" })}
              </h2>
            </div>
            <div className="grid gap-5 md:grid-cols-2">
              {contactFields.map((field) => renderField(field))}
            </div>
          </section>
        </div>

        <aside>
          <section className="rounded-[18px] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-white/10">
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-amber-50 text-amber-600 dark:bg-amber-400/15 dark:text-amber-200">
                <FaHome aria-hidden="true" />
              </span>
              <h2 className="text-lg font-bold text-slate-950 dark:text-white">
                {t("family.parents.edit.tabs.address", { defaultValue: "Address" })}
              </h2>
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-1">
              <DutchAddressFields
                address={values.address}
                zipCode={values.zipCode}
                city={values.city}
                onChange={(field, value) => onChange(field, value)}
                t={t}
                inputClassName={inputClass}
                labelClassName={labelClass}
              />
            </div>
          </section>
        </aside>
      </div>

      {mode === "edit" && (
        <section className="mt-5 rounded-[18px] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-white/10">
          <div className="mb-5 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-violet-50 text-violet-600 dark:bg-violet-400/15 dark:text-violet-200">
              <FaUser aria-hidden="true" />
            </span>
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">
              {t("family.parents.edit.tabs.other", { defaultValue: "Other" })}
            </h2>
          </div>
          <label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/70 p-4 text-sm font-bold text-slate-800 dark:border-white/10 dark:bg-white/5 dark:text-slate-100">
            <input
              type="checkbox"
              name="newsletterOptOut"
              checked={values.newsletterOptOut}
              onChange={(event) => onChange("newsletterOptOut", event.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-900"
            />
            {t("family.parents.newsletter.opt_out", { defaultValue: "Opt-out newsletter" })}
          </label>
          <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-300">
            {t("family.parents.newsletter.opt_out_hint", {
              defaultValue: "The opt-out field controls whether this parent receives newsletter emails.",
            })}
          </p>
        </section>
      )}

      <section className="mt-5 rounded-[18px] border border-indigo-100 bg-gradient-to-r from-indigo-50/80 via-white to-sky-50/60 p-5 shadow-sm dark:border-indigo-400/25 dark:from-indigo-950/25 dark:via-white/5 dark:to-sky-950/20">
        <div className="flex items-start gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-700 dark:bg-indigo-400/15 dark:text-indigo-200">
            <FaUser aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="font-bold text-slate-950 dark:text-white">
              {t("common.next_step", { defaultValue: "Next step" })}
            </p>
            <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {mode === "create"
                ? t("family.parents.new.next_step", {
                    defaultValue: "Vul de verplichte gegevens in en maak de ouder aan.",
                  })
                : t("family.parents.details.next_step", {
                    defaultValue: "Controleer en werk de contactgegevens bij indien nodig.",
                  })}
            </p>
          </div>
        </div>
      </section>

      {!hideActions && (
        <div className="mt-6 flex flex-col-reverse gap-3 border-t border-slate-200/80 pt-5 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 motion-reduce:hover:translate-y-0 dark:border-white/10 dark:bg-white/10 dark:text-slate-100 dark:hover:text-indigo-200"
          >
            <FaTimes aria-hidden="true" />
            {mode === "create" ? t("common.cancel") : t("common.actions.cancel")}
          </button>
          <button
            type="submit"
            className="lumen-glow-sheen inline-flex items-center justify-center gap-2 rounded-full border border-indigo-200/70 bg-indigo-50/80 px-6 py-2.5 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:text-indigo-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:hover:translate-y-0 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-200 dark:hover:text-white"
            disabled={saving}
          >
            <FaSave aria-hidden="true" />
            {saving
              ? t("common.loading")
              : mode === "create"
                ? t("family.parents.new.create")
                : t("common.actions.save_changes", { defaultValue: "Save changes" })}
          </button>
        </div>
      )}
    </form>
  );
}
